import { useState, memo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, Bookmark, Share2, MoreVertical, BookmarkCheck, Loader2, ImageIcon, Trash2, AlertTriangle, Lock, Sparkles } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { truncateText, isAuthenticated } from '@/lib/utils'
import { getGradientForId } from '@/lib/config'
import { motion } from 'framer-motion'
import { newsApi } from '@/services/api'
import { toast } from 'sonner'

const NewsCardComponent = ({ article, variant = 'standard', index = 0, showDelete = false, onDelete }) => {
  const {
    slug,
    alias,
    title,
    excerpt,
    thumbnail,
    resized_featured_image,
    feeder,
    tags = [],
    views = 0,
    comments = 0,
    is_bookmarked = false,
    can_view = true,
    is_curated = false,
    is_state_news = false,
  } = article

  const navigate = useNavigate()
  const [bookmarked, setBookmarked] = useState(is_bookmarked)
  const [bookmarkLoading, setBookmarkLoading] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  // Image priority: thumbnail > resized_featured_image > gradient
  const imageUrl = thumbnail || resized_featured_image
  const hasImage = !!imageUrl
  const gradientStyle = !hasImage ? { background: getGradientForId(alias || slug) } : {}

  const handleBookmark = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!isAuthenticated()) {
      toast.error('Please login to bookmark articles')
      navigate('/login')
      return
    }

    setBookmarkLoading(true)
    const previousState = bookmarked
    setBookmarked(!bookmarked)

    try {
      await newsApi.toggleBookmark(slug)
      toast.success(bookmarked ? 'Removed from bookmarks' : 'Added to bookmarks')
    } catch (error) {
      setBookmarked(previousState)
      toast.error('Failed to update bookmark')
    } finally {
      setBookmarkLoading(false)
    }
  }

  const handleShare = (e) => {
    e.preventDefault()
    e.stopPropagation()
    
    const shareUrl = `${window.location.origin}/posts/${slug}`
    
    if (navigator.share) {
      navigator.share({
        title: title,
        text: excerpt || title,
        url: shareUrl,
      }).catch(() => {
        // User cancelled or error, fallback to copy
        navigator.clipboard.writeText(shareUrl)
        toast.success('Link copied to clipboard!')
      })
    } else {
      navigator.clipboard.writeText(shareUrl)
      toast.success('Link copied to clipboard!')
    }
  }

  const handleDelete = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (onDelete) {
      await onDelete(article)
    }
    setDeleteConfirmOpen(false)
  }

  // Simplified animation - only for initial items, skip for infinite scroll
  const shouldAnimate = index < 10
  
  const cardVariants = shouldAnimate ? {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.2,
        delay: index * 0.03,
      }
    }
  } : {}

  if (variant === 'compact' || variant === 'list') {
    return (
      <motion.article
        variants={shouldAnimate ? cardVariants : undefined}
        initial={shouldAnimate ? "hidden" : false}
        animate={shouldAnimate ? "visible" : false}
        className="group flex gap-5 p-5 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
      >
        {/* Thumbnail */}
        <Link to={`/posts/${slug}`} className="flex-shrink-0">
          {hasImage ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-32 h-24 rounded-lg object-cover"
              loading="lazy"
            />
          ) : (
            <div 
              className="w-32 h-24 rounded-lg flex items-center justify-center"
              style={gradientStyle}
            >
              <ImageIcon className="w-8 h-8 text-white/80" />
            </div>
          )}
        </Link>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <Link to={`/posts/${slug}`}>
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-primary transition-colors leading-snug">
              {title}
            </h3>
          </Link>
          
          {excerpt && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
              {truncateText(excerpt, 150)}
            </p>
          )}
          
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500 dark:text-gray-400">
            <Link to={`/provider/${feeder?.slug}`} className="font-medium hover:text-primary">
              {feeder?.display_name || feeder?.name}
            </Link>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {showDelete ? (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 hover:text-red-600 text-red-500"
              onClick={handleDelete}
              aria-label="Delete bookmark"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className={`h-9 w-9 hover:text-primary ${bookmarked ? 'text-primary' : ''}`}
              onClick={handleBookmark}
              disabled={bookmarkLoading}
              aria-label={bookmarked ? "Remove bookmark" : "Bookmark article"}
            >
              {bookmarkLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : bookmarked ? (
                <BookmarkCheck className="h-4 w-4" />
              ) : (
                <Bookmark className="h-4 w-4" />
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 hover:text-primary"
            onClick={handleShare}
            aria-label="Share article"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirmOpen(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete Bookmark?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Are you sure you want to remove <span className="font-medium">"{title}"</span> from your bookmarks? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setDeleteConfirmOpen(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={confirmDelete}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.article>
    )
  }

  // Standard card variant
  return (
    <motion.article
      variants={shouldAnimate ? cardVariants : undefined}
      initial={shouldAnimate ? "hidden" : false}
      animate={shouldAnimate ? "visible" : false}
      className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-300"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-5 pb-4">
        <div className="flex items-center gap-2">
          {feeder?.logo && (
            <img
              src={feeder.logo}
              alt={feeder.display_name || feeder.name}
              className="w-6 h-6 rounded-full"
            />
          )}
          <div className="flex items-center gap-2 text-sm">
            <Link 
              to={`/provider/${feeder?.slug}`}
              className="font-medium text-gray-900 dark:text-gray-100 hover:text-primary"
            >
              {feeder?.display_name || feeder?.name}
            </Link>
          </div>
        </div>
        
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="More options">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </div>

      {/* Title */}
      <Link to={`/posts/${slug}`} className="block px-5 mt-1">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:text-primary transition-colors leading-tight">
          {title}
        </h2>
      </Link>

      {/* Excerpt */}
      {excerpt && (
        <p className="px-5 mt-4 text-sm text-gray-600 dark:text-gray-400 line-clamp-3 leading-relaxed">
          {truncateText(excerpt, 200)}
        </p>
      )}

      {/* Featured Image */}
      <Link to={`/posts/${slug}`} className="block px-5 mt-5">
        <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100">
          {/* Free tier badges */}
          {is_curated && (
            <div className="absolute top-2 left-2 z-10">
              <Badge className="bg-yellow-500 text-white gap-1">
                <Sparkles className="w-3 h-3" />
                Free Article
              </Badge>
            </div>
          )}
          {!can_view && !is_curated && (
            <div className="absolute top-2 right-2 z-10">
              <Badge variant="secondary" className="bg-slate-700 text-white gap-1">
                <Lock className="w-3 h-3" />
                {is_state_news ? 'Limit Reached' : 'Premium'}
              </Badge>
            </div>
          )}
          
          {hasImage ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div 
              className="w-full h-full flex items-center justify-center"
              style={gradientStyle}
            >
              <ImageIcon className="w-16 h-16 text-white/80" />
            </div>
          )}
        </div>
      </Link>

      {/* Footer Actions */}
      <div className="flex items-center justify-end px-5 py-4 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-3">
          {showDelete ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-9 px-3 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700"
              onClick={handleDelete}
              aria-label="Delete bookmark"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              <span className="text-xs hidden sm:inline">Delete</span>
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className={`h-9 px-3 hover:bg-primary/10 hover:text-primary ${bookmarked ? 'text-primary bg-primary/10' : 'text-gray-600 dark:text-gray-400'}`}
              onClick={handleBookmark}
              disabled={bookmarkLoading}
              aria-label={bookmarked ? "Remove bookmark" : "Bookmark article"}
            >
              {bookmarkLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : bookmarked ? (
                <>
                  <BookmarkCheck className="h-4 w-4 mr-1" />
                  <span className="text-xs hidden sm:inline">Saved</span>
                </>
              ) : (
                <>
                  <Bookmark className="h-4 w-4 mr-1" />
                  <span className="text-xs hidden sm:inline">Save</span>
                </>
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
            onClick={handleShare}
            aria-label="Share article"
          >
            <Share2 className="h-4 w-4 mr-1" />
            <span className="text-xs hidden sm:inline">Share</span>
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirmOpen(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete Bookmark?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Are you sure you want to remove <span className="font-medium">"{title}"</span> from your bookmarks? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirmOpen(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmDelete}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.article>
  )
}

// Memoize NewsCard to prevent unnecessary re-renders
export const NewsCard = memo(NewsCardComponent, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  // Return false if props changed (do re-render)
  return (
    prevProps.article.alias === nextProps.article.alias &&
    prevProps.article.slug === nextProps.article.slug &&
    prevProps.article.is_bookmarked === nextProps.article.is_bookmarked &&
    prevProps.variant === nextProps.variant
  )
})

// Skeleton loader
export function NewsCardSkeleton({ variant = 'standard' }) {
  // List variant skeleton
  if (variant === 'compact' || variant === 'list') {
    return (
      <div className="group flex gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Thumbnail */}
        <div className="w-32 h-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-shimmer flex-shrink-0" />
        
        {/* Content */}
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-full" />
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-3/4" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-1/2" />
        </div>
        
        {/* Actions */}
        <div className="flex flex-col gap-2">
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer" />
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer" />
        </div>
      </div>
    )
  }

  // Card variant skeleton (standard)
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Image */}
      <div className="aspect-video bg-gray-200 dark:bg-gray-700 animate-shimmer" />
      
      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Provider */}
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full animate-shimmer" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-32" />
        </div>
        
        {/* Title */}
        <div className="space-y-2">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-full" />
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-4/5" />
        </div>
        
        {/* Excerpt */}
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-full" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer w-3/4" />
        </div>
      </div>
      
      {/* Footer */}
      <div className="flex items-center justify-end px-4 py-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer" />
          <div className="w-16 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-shimmer" />
        </div>
      </div>
    </div>
  )
}
